import { useRef, useState, type DragEvent } from 'react';
import { openUploadSession, uploadFile, type DatasetVersion } from '../../api/datasets';
import { toFormErrors } from '../../auth/errors';
import { Alert, Button } from '../ui';
import './UploadDropzone.css';

const ACCEPTED = '.csv,.tsv,.txt,.xlsx,.json,.ndjson,.jsonl';

export interface UploadDropzoneProps {
  /** Adding a version to an existing dataset instead of creating one. */
  datasetId?: string;
  onUploaded: (version: DatasetVersion) => void;
}

/**
 * Drag-and-drop upload.
 *
 * The client checks nothing that matters: the backend decides what the file
 * really is, how large it may be, and whether it is accepted at all (Rule 7).
 * What happens here is purely to make the wait legible.
 */
export function UploadDropzone({ datasetId, onUploaded }: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  async function send(file: File) {
    setError(null);
    setFileName(file.name);
    setProgress(0);

    try {
      const session = await openUploadSession({
        filename: file.name,
        ...(datasetId ? { dataset_id: datasetId } : {}),
      });
      const version = await uploadFile(session, file, setProgress);
      setProgress(null);
      setFileName(null);
      onUploaded(version);
    } catch (caught) {
      const mapped = toFormErrors(caught);
      setError(mapped.fields['filename'] ?? mapped.message ?? 'The upload failed.');
      setProgress(null);
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      void send(file);
    }
  }

  const isUploading = progress !== null;

  return (
    <div className="sc-dropzone__wrap">
      <div
        className={`sc-dropzone ${isDragging ? 'sc-dropzone--over' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <p className="sc-dropzone__title">Drop a file here</p>
        <p className="sc-dropzone__hint">CSV, XLSX or JSON — up to the size your plan allows.</p>

        <Button
          variant="secondary"
          onClick={() => inputRef.current?.click()}
          isLoading={isUploading}
          loadingLabel="Uploading"
        >
          Choose a file
        </Button>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          className="sc-visually-hidden"
          aria-label="Dataset file"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              void send(file);
            }
            // Allow re-selecting the same file after a failure.
            event.target.value = '';
          }}
        />
      </div>

      {isUploading && (
        <div className="sc-dropzone__progress" role="status" aria-live="polite">
          <div className="sc-dropzone__bar">
            <div className="sc-dropzone__fill" style={{ width: `${progress}%` }} />
          </div>
          <span>
            Uploading {fileName} — {progress}%
          </span>
        </div>
      )}

      {error && (
        <Alert tone="danger" title="That file was not accepted">
          {error}
        </Alert>
      )}
    </div>
  );
}
